import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface Slide {
  id: number;
  backgroundImage: string;
  title: string;
  subtitle: string;
  chips: string[];
  buttons: {
    primary: { text: string; link: string };
    secondary: { text: string; link: string };
  };
  isH1?: boolean;
}

const slides: Slide[] = [
  {
    id: 1,
    backgroundImage: "https://public.readdy.ai/ai/img_res/f880576b684af81220965b906cf91e56.jpg",
    title: "نبني البنية التحتية… ونصوغ الثقة",
    subtitle: "الإنشاءات والطرق: تسوية وحفر، طبقات أساس، خرسانة/أسفلت، أرصفة وإنترلوك، كهرباء وإنارة، شبكات ماء/صرف/اتصالات",
    chips: ["تنفيذ وفق المواصفات", "تسليم في الموعد", "جودة مضمونة"],
    buttons: {
      primary: { text: "طلب عرض سعر", link: "/services/construction" },
      secondary: { text: "عرض الخدمة", link: "/services/construction" }
    },
    isH1: true
  },
  {
    id: 2,
    backgroundImage: "https://readdy.ai/api/search-image?query=Oil%20and%20gas%20industrial%20facility%20with%20pipeline%20network%20and%20storage%20tanks%2C%20petroleum%20refinery%20complex%20with%20modern%20infrastructure%2C%20industrial%20pipes%20and%20valves%20system%2C%20professional%20oil%20field%20operations%20in%20Kuwait%2C%20sunset%20lighting%20creating%20dramatic%20industrial%20atmosphere%2C%20high%20quality%20professional%20photography&width=1920&height=1080&seq=hero-petroleum-slide2&orientation=landscape",
    title: "حلول متكاملة لقطاع البترول",
    subtitle: "توريد معدات ومواد، O&M، خطوط وأنابيب وخزانات API 650/653، فحوص NDT، امتثال HSE",
    chips: ["Supply Chain", "NDT/API", "HSE"],
    buttons: {
      primary: { text: "طلب عرض سعر", link: "/services/oil" },
      secondary: { text: "عرض الخدمة", link: "/services/oil" }
    }
  },
  {
    id: 3,
    backgroundImage: "https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20with%20gold%20bars%20and%20security%20technology%20equipment%2C%20modern%20office%20environment%20with%20diverse%20professionals%20discussing%20projects%2C%20blend%20of%20precious%20metals%20trading%20and%20defense%20technology%20consulting%2C%20sophisticated%20lighting%20and%20modern%20interior%20design%2C%20high%20quality%20professional%20photography&width=1920&height=1080&seq=hero-multi-services-slide3&orientation=landscape",
    title: "ثقة تُصاغ من الخبرة",
    subtitle: "ذهب بمعايير سويسرية | تسليح الجهات الحكومية | استشارات فنية ومالية وإدارة عقود",
    chips: ["KYC/AML", "Export Control", "Feasibility"],
    buttons: {
      primary: { text: "عرض الخدمات", link: "/services" },
      secondary: { text: "مراسلة عبر البريد", link: "mailto:info@vipatradingco.com" }
    }
  }
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    } else if (isRightSwipe) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleButtonClick = (link: string) => {
    if (link.startsWith('mailto:')) {
      window.location.href = link;
    } else {
      window.location.href = link;
    }
  };

  return (
    <section 
      className="relative h-[80vh] sm:h-[60vh] md:h-[80vh] overflow-hidden"
      onMouseEnter={() => setIsPlaying(false)}
      onMouseLeave={() => setIsPlaying(true)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url('${slide.backgroundImage}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: index === currentSlide ? 'scale(1)' : 'scale(1.05)',
            transition: 'transform 5s ease-out, opacity 1s ease-in-out'
          }}
        >
          {/* Red gradient overlay from left to right */}
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(90deg, rgba(231, 27, 37, 0.6) 0%, rgba(231, 27, 37, 0.3) 50%, rgba(0, 0, 0, 0.2) 100%)`
            }}
          ></div>
          
          <div className="container mx-auto px-4 h-full relative z-10">
            <div className="flex items-center h-full">
              <div className="max-w-4xl text-right sm:text-center md:text-right text-white py-24">
                {slide.isH1 ? (
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg">
                    {slide.title.split('…').map((part, i) => (
                      <span key={i}>
                        {i === 0 ? part : (
                          <>
                            <span className="text-[#E71B25]">…{part}</span>
                          </>
                        )}
                      </span>
                    ))}
                  </h1>
                ) : (
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg">
                    <span className="text-[#E71B25]">{slide.title}</span>
                  </h2>
                )}
                
                <p className="text-lg md:text-xl mb-6 leading-relaxed text-gray-100 drop-shadow-md max-w-3xl">
                  {slide.subtitle}
                </p>
                
                {/* Chips */}
                <div className="flex flex-wrap gap-2 mb-8 justify-start sm:justify-center md:justify-start">
                  {slide.chips.map((chip, chipIndex) => (
                    <span 
                      key={chipIndex}
                      className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium border border-white/30"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
                
                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-start sm:justify-center md:justify-start">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="shadow-lg whitespace-nowrap"
                    onClick={() => handleButtonClick(slide.buttons.primary.link)}
                  >
                    {slide.buttons.primary.text}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#595A5C] shadow-lg whitespace-nowrap"
                    onClick={() => handleButtonClick(slide.buttons.secondary.link)}
                  >
                    {slide.buttons.secondary.text}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 z-20 w-12 h-12 flex items-center justify-center"
        aria-label="الشريحة السابقة"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 z-20 w-12 h-12 flex items-center justify-center"
        aria-label="الشريحة التالية"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>

      {/* Dots/Bullets */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-[#E71B25] scale-125' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`الشريحة ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20 z-20">
        <div 
          className="h-full bg-[#E71B25] transition-all duration-100 ease-linear"
          style={{
            width: isPlaying ? `${((currentSlide + 1) / slides.length) * 100}%` : `${((currentSlide + 1) / slides.length) * 100}%`
          }}
        />
      </div>
    </section>
  );
}